import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import {
  countryOptions,
  getStateOptionsForCountry,
} from "../../utility/constants";

const LocationFilterDialog = ({
  open,
  onClose,
  initialCountry,
  initialState,
  onApply,
}) => {
  const [pendingCountry, setPendingCountry] = useState(initialCountry);
  const [pendingState, setPendingState] = useState(initialState);

  useEffect(() => {
    if (open) {
      setPendingCountry(initialCountry);
      setPendingState(initialState);
    }
  }, [open, initialCountry, initialState]);

  const stateOptions = pendingCountry
    ? getStateOptionsForCountry(pendingCountry)
    : null;

  const handleCountrySelect = (value) => {
    setPendingCountry((prev) => (prev === value ? null : value));
    setPendingState(null);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 700, fontSize: 16, pb: 1 }}>
        Filter by location
      </DialogTitle>
      <DialogContent>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: "#6B7280", letterSpacing: 0.3 }}
        >
          COUNTRY
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1, mb: 2.5 }}>
          {countryOptions.map((opt) => {
            const selected = pendingCountry === opt.value;
            return (
              <Chip
                key={opt.value}
                label={opt.label}
                size="small"
                onClick={() => handleCountrySelect(opt.value)}
                sx={{
                  cursor: "pointer",
                  bgcolor: selected ? "#046287" : "#F3F4F6",
                  color: selected ? "white" : undefined,
                  fontWeight: selected ? 600 : 400,
                  "&:hover": { bgcolor: selected ? "#046287" : undefined },
                }}
              />
            );
          })}
        </Box>

        {stateOptions && (
          <>
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: "#6B7280", letterSpacing: 0.3 }}
            >
              STATE / PROVINCE
            </Typography>
            <List
              dense
              sx={{
                maxHeight: 280,
                overflow: "auto",
                mt: 0.5,
                border: "1px solid #F3F4F6",
                borderRadius: 2,
              }}
            >
              {stateOptions.map((opt) => {
                const selected = pendingState === opt.value;
                return (
                  <ListItemButton
                    key={opt.value}
                    selected={selected}
                    onClick={() =>
                      setPendingState((prev) =>
                        prev === opt.value ? null : opt.value,
                      )
                    }
                    sx={{
                      borderBottom: "1px solid #F3F4F6",
                      "&.Mui-selected": {
                        bgcolor: "transparent",
                        color: "#046287",
                      },
                      "&.Mui-selected:hover": { bgcolor: "#f0f9ff" },
                    }}
                  >
                    <ListItemText
                      primary={opt.label}
                      primaryTypographyProps={{
                        fontSize: 14,
                        fontWeight: selected ? 600 : 400,
                        color: selected ? "#046287" : "#111827",
                      }}
                    />
                    {selected && (
                      <CheckIcon sx={{ fontSize: 18, color: "#046287" }} />
                    )}
                  </ListItemButton>
                );
              })}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={() => {
            onApply({ country: null, state: null });
            onClose();
          }}
          sx={{
            flex: 1,
            borderColor: "#D1D5DB",
            color: "#374151",
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          Clear
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            onApply({
              country: pendingCountry || null,
              state: pendingState || null,
            });
            onClose();
          }}
          sx={{
            flex: 2,
            bgcolor: "#046287",
            textTransform: "none",
            fontWeight: 600,
            "&:hover": { bgcolor: "#035070" },
          }}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LocationFilterDialog;
